import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { THEMED_SURFACE_COLORS as C, ThemedPanel } from '../components/ui/ThemedSurface';

const TERM_SECTION_KEYS = [
  'acceptance',
  'definitions',
  'account',
  'services',
  'assets',
  'trading',
  'fees',
  'rewards',
  'conduct',
  'risks',
  'suspension',
  'liability',
  'privacy',
  'changes',
  'law',
  'contact',
] as const;

function TermSection({ index, sectionKey }: { index: number; sectionKey: (typeof TERM_SECTION_KEYS)[number] }) {
  const { t } = useTranslation();
  const paragraphs = t(`terms.sections.${sectionKey}.body`, { returnObjects: true }) as string[];

  return (
    <ThemedPanel className="p-4">
      <div className="mb-3 flex items-start gap-3">
        <span
          className="flex size-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold"
          style={{ background: C.chipBg, color: C.gold, border: `1px solid ${C.gold}44` }}
        >
          {index + 1}
        </span>
        <h2 className="text-sm font-semibold leading-snug" style={{ color: C.text }}>
          {t(`terms.sections.${sectionKey}.title`)}
        </h2>
      </div>
      <div className="flex flex-col gap-2.5 pl-9">
        {Array.isArray(paragraphs) && paragraphs.map((paragraph, paragraphIndex) => (
          <p key={paragraphIndex} className="text-xs leading-relaxed" style={{ color: C.muted }}>
            {paragraph}
          </p>
        ))}
      </div>
    </ThemedPanel>
  );
}

export function Terms() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader title={t('terms.title')} onBack={() => navigate(-1)} theme={C} />
        <div
          className="flex flex-col gap-4 overflow-y-auto px-5 pt-[70px]"
          style={{ height: 'calc(100% - 70px)', paddingBottom: '24px' }}
        >
          <ThemedPanel className="p-4">
            <div className="text-xs font-medium" style={{ color: C.gold }}>
              {t('terms.lastUpdated')}
            </div>
            <p className="mt-3 text-xs leading-relaxed" style={{ color: C.muted }}>
              {t('terms.intro')}
            </p>
          </ThemedPanel>

          {TERM_SECTION_KEYS.map((sectionKey, index) => (
            <TermSection key={sectionKey} index={index} sectionKey={sectionKey} />
          ))}

          <p className="px-1 text-center text-[10px] leading-relaxed" style={{ color: C.muted }}>
            {t('terms.footer')}
          </p>
        </div>
      </div>
    </div>
  );
}

export default Terms;
